import Icon from "app/components/shared/Icon";
import Emojify from "app/components/shared/Emojify";
import FriendlyTime from "app/components/shared/FriendlyTime";
import AnchoredPopover from "app/components/shared/Popover/anchored";
import { getDateString } from "app/lib/date";
import { capitalize } from "lodash";
import { StyledAnchoredPopover } from "../../shared/Activity/Bar";
import { SuiteActivityProps } from ".";

const MIN_HEIGHT_PERCENT = 3;

type Props = {
  activity: SuiteActivityProps;
  height: number;
};

const calculateStackedBarHeights = ({ activity }): [number, number, number] => {
  const { passed_duration, failed_duration, duration } = activity;
  const remaining_duration = duration - (passed_duration + failed_duration);

  const applyMinHeight = (value) => (value > 0 ? Math.max(MIN_HEIGHT_PERCENT, value) : 0);

  const passed = applyMinHeight((passed_duration / duration) * 100.0);
  const failed = applyMinHeight((failed_duration / duration) * 100.0);
  const other = applyMinHeight((remaining_duration / duration) * 100.0);

  const total = passed + failed + other;

  if (total > 100) {
    // Normalize while preserving ratio and minimums
    const scale = 100 / total;
    const passedHeight = Math.round(passed * scale);
    const failedHeight = Math.round(failed * scale);

    const otherHeight = 100 - passedHeight - failedHeight; // Ensure 100% height

    return [passedHeight, failedHeight, otherHeight];
  }

  return [passed, failed, other];
};

const StackedBars = ({ activity, height }: Props) => {
  const [passedHeight, failedHeight, otherStatusHeights] = calculateStackedBarHeights({ activity });
  const linkLabel = `Go to ${activity.name}. ${capitalize(
    activity.result,
  )} on ${getDateString(activity.createdAt)} — ${activity.humanDuration}`;

  return (
    <div
      data-testid="stacked-bar-container"
      className="flex flex-col items-start gap-y-[1px]"
      style={{ height: `${Math.round(height * 100.0)}%`, width: 12 }}
    >
      <StyledAnchoredPopover as={AnchoredPopover} className="flex" data-testid="bar" width={300}>
        <a
          aria-label={linkLabel}
          href={activity.url}
          className="block relative h-full w-full no-title-tooltip"
        >
          {failedHeight > 0 && (
            <span
              data-testid="failed-styled-bar"
              className="w-full h-full transition-height duration-200 ease-in block rounded-t-[2px] ta-bg-red hover-ta-bg-red"
              style={{
                height: `${failedHeight}%`,
              }}
            />
          )}
          {passedHeight > 0 && (
            <span
              data-testid="passed-styled-bar"
              className="w-full h-full transition-height duration-200 ease-in block ta-bg-green hover-ta-bg-green"
              style={{
                height: `${passedHeight}%`,
              }}
            />
          )}

          {otherStatusHeights > 0 && (
            <span
              data-testid="other-styled-bar"
              className="w-full h-full transition-height duration-200 ease-in block bg-gray"
              style={{
                height: `${otherStatusHeights}%`,
              }}
            />
          )}
        </a>

        <div className="flex flex-col gap-1 px-2 py-1">
          <span className="block font-medium break-words">
            <Emojify text={activity.name} />
          </span>

          {activity.buildNumber && (
            <div className="dark-gray small">Build #{activity.buildNumber}</div>
          )}

          {activity.branch && (
            <div className="dark-gray small">
              <Icon icon="custom/outline/14x/branch" style={{ height: 14, width: 14 }} />{" "}
              {activity.branch}
            </div>
          )}

          <span className="block small dark-gray border-b">
            {capitalize(activity.result)}{" "}
            <FriendlyTime value={activity.createdAt} clickable={false} /> - {activity.humanDuration}
          </span>
          <span className="block small dark-gray">
            <strong>Execution results</strong>
            <br />
            {activity.failed_execution_count} Failed - {activity.humanFailedDuration}
            <br />
            {activity.passed_execution_count} Passed - {activity.humanPassedDuration}
            <br />
            {activity.skipped_execution_count > 0 && (
              <>
                {activity.skipped_execution_count} Skipped - {activity.humanSkippedDuration}
                <br />
              </>
            )}
            {activity.pending_execution_count > 0 && (
              <>
                {activity.pending_execution_count} Pending - {activity.humanPendingDuration}
                <br />
              </>
            )}
            {activity.unknown_execution_count > 0 && (
              <>
                {activity.unknown_execution_count} Unknown - {activity.humanUnknownDuration}
                <br />
              </>
            )}
          </span>
        </div>
      </StyledAnchoredPopover>
    </div>
  );
};

export default StackedBars;
